import {
  ChangeEvent,
  FC,
  Fragment,
  MouseEvent,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useRecoilValue, useRecoilValueLoadable, useSetRecoilState} from 'recoil';
import {useFormContext} from 'react-hook-form';
import {toast} from 'react-toastify';
import {get, omit} from 'lodash';

import {Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import {useCompanyTariffGuard, useRefreshCompanyTariff} from '../../../../modules/company-tariff';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import {EventBusContext} from '../../../../providers/EventBus';

import {useFileUpload} from '../../../../hooks/useFileUpload';

import {IDocument} from '../../../../shared/types/document.types';
import {ISelectedDocToReplace} from './utils';

import {DocumentService} from '../../../../api/services/document.service';

import {colors} from '../../../../shared/colors';

import {
  contentTreeQueryTrigger,
  docsToReplaceState,
  filesFormNameMapState,
  serviceRequestActiveTabState,
} from '../../../../store/service-requests/atoms';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {useParams} from 'react-router-dom';
import {ModalContainer} from '../../../modals/ModalContainer';
import {ConfirmAction} from '../../../modals/ConfirmAction';
import {contentTreeFilesQueryFamily} from '../../../../store/service-requests/selectors';
import {createObjectURL} from '../../../../shared/helpers/resolveAndDownloadBlob';
import {DateHelpers} from '../../../../shared/helpers/dateHelpers';
import {InputTypeEnum} from '../../../../shared/types/workContent.types';
import {WorkDataInformationEdit} from './WorkDataInformationEdit';

interface IWorkDataAudio {
  item: {
    files?: IDocument[];
    text: string;
    formName: string;
    mandatory?: boolean;
    maxFilesAllowed?: number;
    availableFileExtensions?: string;
  };
  isEditable: boolean;
  isTextVisible?: boolean;
  isTextEmptyVisible?: boolean;
  isMultipleInput?: boolean;
}

let currentAudioRef: RefObject<HTMLAudioElement> | null = null;
const AudioItem: FC<{
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
  url: string;
  fileName: string;
  item?: IDocument;
}> = ({url, fileName, onClick, item}) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const handlePlay = () => {
    if (!!currentAudioRef && currentAudioRef?.current) {
      if (currentAudioRef?.current !== audioRef?.current && !currentAudioRef.current?.paused) {
        (currentAudioRef as RefObject<HTMLAudioElement>).current?.pause();
      }
    }
    currentAudioRef = audioRef;
  };

  return (
    <Stack direction="column" spacing={1} sx={{marginBottom: '12px'}}>
      <Tooltip
        placement="bottom-start"
        title={
          !item ? (
            fileName
          ) : (
            <Fragment>
              <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                <b>Дата создания:</b> {DateHelpers.formatToRu(get(item, `createdDate`))}
              </Typography>
              <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                <b>Автор:</b> {get(item, `createdFio`) || '-'}
              </Typography>
              <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                <b>Комментарий:</b> {get(item, `description`) || '-'}
              </Typography>
            </Fragment>
          )
        }
      >
        <Typography
          onClick={onClick}
          color="blue"
          sx={{cursor: onClick ? 'pointer' : 'initial'}}
          variant="subtitle2"
          noWrap
          textOverflow="ellipsis"
        >
          {fileName}
        </Typography>
      </Tooltip>
      <audio src={url} controls ref={audioRef} onPlay={handlePlay} />
    </Stack>
  );
};

const getMoreThanAllowedText = (maxFiles: number) =>
  `Количество выбранных/загруженных аудиофайлов больше допустимого значения. Максимальное значение: ${maxFiles}`;

const isAudio = (mimeType: string) => {
  if (!mimeType) {
    return false;
  }

  const generalType = mimeType.split('/')[0].toUpperCase();

  return InputTypeEnum.AUDIO === generalType;
};

export const WorkDataAudio: FC<IWorkDataAudio> = ({
  item,
  isEditable,
  isTextVisible = true,
  isTextEmptyVisible = true,
  isMultipleInput = false,
}) => {
  const prevFiles = item.files ? item.files.filter((i) => isAudio(i.mimeType)) : [];

  const {id} = useParams();
  const activeTab = useRecoilValue(serviceRequestActiveTabState);
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const refreshCompanyTariff = useRefreshCompanyTariff();
  const inputRef = useRef<HTMLInputElement>(null);
  const {files, fileNames, setFiles, clearAllFiles, removeFile} = useFileUpload();
  const {
    files: filesToReplace,
    fileNames: fileNamesToReplace,
    setFiles: setFilesToReplace,
    clearAllFiles: clearAllFilesToReplace,
  } = useFileUpload();
  const {contents, state} = useRecoilValueLoadable(
    contentTreeFilesQueryFamily(prevFiles.length ? prevFiles.map((doc) => doc.id) : null),
  );
  const setFilesFormNameMap = useSetRecoilState(filesFormNameMapState);
  const {bus} = useContext(EventBusContext);
  const [selectedToReplace, setSelectedToReplace] = useState<ISelectedDocToReplace | null>(null);
  const [replaceMode, setReplaceMode] = useState(false);
  const setDocsToReplace = useSetRecoilState(docsToReplaceState);
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const triggerContentTreeQuery = useSetRecoilState(contentTreeQueryTrigger);
  const isOnlyUploadMode = Boolean(activeTab?.onlyUpload);
  const isEditMode = isEditable && activeTab?.editMode;
  const canModifyExistingFiles = isEditMode && !isOnlyUploadMode;

  const handleFileSelect = () => {
    if (!checkTariff(true)) return;

    inputRef.current?.click();
  };

  const closeModal = () => {
    setOpen(false);
    setIdToDelete(null);
  };

  const deleteHandler = async (setLoading: (arg: boolean) => void) => {
    if (!idToDelete || !id) {
      return;
    }

    try {
      setLoading(true);
      await ServiceRequestService.deleteDocument(Number(id), idToDelete);
      refreshCompanyTariff();
      triggerContentTreeQuery((v) => v + 1);
      closeModal();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  const isFilesExist = item.files?.length;

  const toggleSelected = (url: string, index: number) => {
    if (!prevFiles || !prevFiles?.length || !canModifyExistingFiles) {
      return;
    }

    setSelectedToReplace((prev) =>
      prev?.oldSrc === url ? prev : {oldSrc: url, newSrc: null, docId: prevFiles[index].id},
    );
  };

  const {setValue, getValues} = useFormContext();

  const defaultValueRef = useRef(getValues(item.formName));

  const onDownLoad = async (e: MouseEvent<HTMLSpanElement>, doc: IDocument) => {
    try {
      e.preventDefault();

      const res = await DocumentService.getContent(doc.id);

      const url = URL.createObjectURL(res.data);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.fileName;
      anchor.style.display = 'none';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      bus?.emit('error', [err]);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget?.files && !replaceMode) {
      const filesFromEvent = e.currentTarget?.files || [];
      const prevFiles = getValues(item.formName) as string | File[];

      const filesArr = Array.from(filesFromEvent);
      const downloadedFilesLength = item?.files?.length ?? 0;
      const formFilesLength = isMultipleInput && Array.isArray(prevFiles) ? prevFiles.length : 0;

      const isMoreThanAllowed = item.maxFilesAllowed
        ? filesArr.length > item.maxFilesAllowed ||
          filesArr.length + downloadedFilesLength > item.maxFilesAllowed ||
          filesArr.length + downloadedFilesLength + formFilesLength > item.maxFilesAllowed
        : false;

      if (isMoreThanAllowed) {
        toast.error(getMoreThanAllowedText(item.maxFilesAllowed as number), {containerId: 'default'});

        return;
      }
    }

    if (replaceMode) {
      if (selectedToReplace && e.currentTarget?.files?.length) {
        setSelectedToReplace((prev) =>
          prev ? {...prev, newSrc: URL.createObjectURL((e.currentTarget?.files as FileList)[0])} : prev,
        );
      }

      setFilesToReplace(e);
    } else {
      setFiles(e);
    }
  };

  const changeInputFileList = (fileName: string) => {
    if (!inputRef.current) return;
    const fileList = inputRef.current.files || [];
    const dt = new DataTransfer();

    for (let i = 0; i < fileList.length; i++) {
      if (fileList[i].name !== fileName) {
        dt.items.add(fileList[i]);
      }
    }
    inputRef.current.files = dt.files;
  };

  const onClearToReplace = () => {
    if (!inputRef.current) return;
    const dt = new DataTransfer();
    inputRef.current.files = dt.files;
  };

  const onRemove = (fileName: string) => {
    changeInputFileList(fileName);
    removeFile(fileName);
  };

  useEffect(() => {
    if (!canModifyExistingFiles && replaceMode) {
      setDocsToReplace((prev) =>
        selectedToReplace?.docId ? prev.filter((doc) => doc.docId !== selectedToReplace.docId) : prev,
      );
      setSelectedToReplace(null);
      setReplaceMode(false);
      clearAllFilesToReplace();
      if (inputRef.current) {
        const dt = new DataTransfer();
        inputRef.current.files = dt.files;
      }
    }
  }, [canModifyExistingFiles, replaceMode, clearAllFilesToReplace, selectedToReplace, setDocsToReplace]);

  const renderNonEditForm = () =>
    state === 'loading' ? (
      <Stack sx={{height: '50px', width: '50px', alignItems: 'center', justifyContent: 'center'}}>
        <CircularProgress size="small" sx={{height: '22px', width: '22px'}} />
      </Stack>
    ) : state === 'hasValue' && prevFiles.length && Array.isArray(contents) ? (
      contents.map((url, ind) => {
        const file = prevFiles?.[ind] as IDocument;

        return url ? (
          <AudioItem
            key={file.id}
            url={url}
            fileName={file.fileName}
            onClick={(e) => onDownLoad(e, file)}
            item={file}
          />
        ) : (
          <Typography variant="subtitle2">Ошибка загрузки</Typography>
        );
      })
    ) : isTextEmptyVisible ? (
      <Typography variant="subtitle2">Не загружено ни одного файла</Typography>
    ) : null;

  const renderEditForm = () => {
    let arrDownloadedAudios: ReactNode[] = [];
    let arrLocalAudios: ReactNode[] = [];

    if (state === 'loading') {
      return (
        <Stack sx={{height: '50px', width: '50px', alignItems: 'center', justifyContent: 'center'}}>
          <CircularProgress size="small" sx={{height: '22px', width: '22px'}} />
        </Stack>
      );
    }

    if (files.length) {
      arrLocalAudios = arrLocalAudios.concat(
        files.map((file, ind) => {
          const url = createObjectURL(file);

          return (
            <Stack key={file.name + file.lastModified} direction="row" spacing={1}>
              <AudioItem url={url} fileName={fileNames[ind]} />
              <Stack direction="row" gap={2}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(file.name);
                  }}
                  sx={{width: 20, height: 20}}
                >
                  <HighlightOffIcon />
                </IconButton>
                {canModifyExistingFiles && <WorkDataInformationEdit document={prevFiles?.[ind]} />}
              </Stack>
            </Stack>
          );
        }),
      );
    }

    if (state === 'hasValue' && Array.isArray(prevFiles) && Array.isArray(contents)) {
      arrDownloadedAudios = arrDownloadedAudios.concat(
        contents.map((url, ind) => {
          const file = prevFiles?.[ind] as IDocument;
          const selected = selectedToReplace?.oldSrc === (file.link || file.path);

          const fileName = selected && fileNamesToReplace[0] ? fileNamesToReplace[0] : file.fileName;

          const handleClick = () => {
            if (replaceMode) {
              toggleSelected((file.link || file.path) as string, ind);
            }
          };

          return url ? (
            <Stack direction="row" key={file.id} spacing={1} sx={{marginBottom: '10px'}}>
              <Box
                sx={{
                  borderRadius: '6px',
                  border: selected ? `2px solid blue` : undefined,
                  cursor: replaceMode ? 'pointer' : 'initial',
                  padding: replaceMode ? 2 : undefined,
                }}
                onClick={handleClick}
              >
                <AudioItem
                  key={file.id}
                  url={url}
                  fileName={fileName}
                  onClick={!replaceMode ? (e) => onDownLoad(e, file) : undefined}
                  item={file}
                />
              </Box>
              {canModifyExistingFiles && (
                <Stack direction="row" gap={2}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdToDelete(file.id as string);
                      setOpen(true);
                    }}
                    sx={{width: 20, height: 20}}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                  <WorkDataInformationEdit document={file} />
                </Stack>
              )}
            </Stack>
          ) : (
            <Typography variant="subtitle2">Ошибка загрузки</Typography>
          );
        }),
      );
    }

    const showSplash = !arrDownloadedAudios.length && !arrLocalAudios.length;

    return (
      <>
        {showSplash && isTextEmptyVisible && <Typography variant="subtitle2">Не загружено ни одного файла</Typography>}
        {arrDownloadedAudios}
        {arrLocalAudios}
      </>
    );
  };

  useEffect(() => {
    if (selectedToReplace && filesToReplace.length) {
      setDocsToReplace((prev) =>
        prev
          .filter((i) => i.docId !== selectedToReplace.docId)
          .concat({...omit(selectedToReplace, ['newSrc']), file: filesToReplace[0]}),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToReplace?.newSrc]);

  useEffect(() => {
    if (selectedToReplace?.oldSrc) {
      clearAllFilesToReplace();
      onClearToReplace();
      setDocsToReplace((prev) => prev.filter((i) => i.docId !== selectedToReplace.docId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToReplace?.oldSrc]);

  useEffect(() => {
    setFilesFormNameMap((prev) => {
      prev.set(item.formName, true);

      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeTab?.editMode) {
      clearAllFiles();
      clearAllFilesToReplace();
      onClearToReplace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab?.editMode]);

  useEffect(() => {
    const syncSelectedFilesToForm = (selectedFiles: File[], needToConcat = true) => {
      const prevFiles = getValues(item.formName) as File[];

      const newValue =
        isMultipleInput && Array.isArray(prevFiles)
          ? Array.from(
              new Set(
                prevFiles
                  .filter((i) => !selectedFiles.find((file) => file.name === i.name))
                  .concat(needToConcat ? selectedFiles : []),
              ),
            )
          : selectedFiles.length
            ? selectedFiles
            : defaultValueRef.current;

      setValue(item.formName, newValue, {shouldDirty: true});
    };

    syncSelectedFilesToForm(files);

    return () => {
      syncSelectedFilesToForm(files, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <Stack gap={1.5} sx={{flexWrap: 'wrap'}} direction="column">
      <Stack direction="row">
        {item.mandatory ? (
          <Typography variant="subtitle2" color="red">
            *
          </Typography>
        ) : null}
        {isTextVisible && (
          <Typography
            sx={{color: colors['icons--primary'], width: 'fit-content', maxWidth: activeTab?.editMode ? 290 : 320}}
            textOverflow="ellipsis"
            noWrap
            variant="subtitle2"
          >
            {item.text}
          </Typography>
        )}
      </Stack>
      <Stack direction="column">{!isEditMode ? renderNonEditForm() : renderEditForm()}</Stack>
      {isEditMode ? (
        <Stack spacing={1} direction="row">
          {!replaceMode && (
            <Tooltip title="Выбрать файлы для загрузки">
              <IconButton
                onClick={handleFileSelect}
                disabled={tariffLoading}
                aria-label="Загрузить документы"
                size="small"
              >
                <UploadIcon />
              </IconButton>
            </Tooltip>
          )}
          {canModifyExistingFiles && isFilesExist ? (
            <Stack spacing={1} direction="row">
              <Button
                onClick={() => {
                  if (!replaceMode) {
                    setReplaceMode(true);
                  } else {
                    handleFileSelect();
                  }
                }}
                disabled={tariffLoading || (replaceMode && !selectedToReplace)}
              >
                {!replaceMode ? 'Замена файла' : 'Выбрать'}
              </Button>
              {replaceMode && (
                <Button
                  onClick={() => {
                    setSelectedToReplace(null);
                    setReplaceMode(false);
                    clearAllFilesToReplace();
                    onClearToReplace();
                  }}
                >
                  Отмена
                </Button>
              )}
            </Stack>
          ) : null}
        </Stack>
      ) : null}

      {isEditMode ? (
        <input
          type="file"
          ref={inputRef}
          style={{display: 'none'}}
          onChange={onChange}
          multiple={!replaceMode}
          accept="audio/*"
        />
      ) : null}
      {open && (
        <ModalContainer
          sideEffect={deleteHandler}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={open}
              handleClose={closeModal}
              handleSideEffect={sideEffect}
              title="Вы уверены? После данного действия файл будет удален навсегда"
              buttonTitle="Удалить"
            />
          )}
        />
      )}
    </Stack>
  );
};
